import React from 'react';
import { SalaryStep } from "./SalaryStep";
import { ExpensesStep } from "./ExpensesStep";
import { TimeOffStep } from "./TimeOffStep";
import { RatePrompt } from "./RatePrompt";
import RateWhispererResultsStep from "./ResultsStep";
import { calculateResults } from "./utils";
import type { FormData } from "./types";

interface RateWhispererRendererProps {
  currentStep: number;
  formData: FormData;
  promptMode: boolean;
  onInputChange: (field: string, value: string, category?: 'expenses' | 'timeOff') => void;
  onNext: () => void;
  onStartOver: () => void;
  onResultsFromPrompt: (formData: FormData) => void;
}

export const RateWhispererRenderer = ({
  currentStep,
  formData,
  promptMode,
  onInputChange,
  onNext,
  onStartOver,
  onResultsFromPrompt,
}: RateWhispererRendererProps) => {
  const calculations = calculateResults(formData);
  const displayCalculations = {
    totalMonthlyExpenses: calculations.monthlyExpenses,
    workingHours: calculations.workHours,
    hourlyRate: calculations.hourlyRate,
    effectiveHours: calculations.effectiveHours,
    totalIncome: calculations.totalIncome
  };

  // Handle results from prompt analysis
  const handlePromptResults = (extractedFormData: FormData, calculationsResult: any) => {
    onResultsFromPrompt(extractedFormData);
  };

  // If in prompt mode and first step, show the prompt interface
  if (promptMode && currentStep === 1) {
    return (
      <RatePrompt onResultsCalculated={handlePromptResults} />
    );
  }

  // Otherwise, show the regular step by step interface
  switch (currentStep) {
    case 1:
      return (
        <SalaryStep
          formData={formData}
          formErrors={{}}
          onInputChange={onInputChange}
          onNext={onNext}
        />
      );
    case 2:
      return (
        <ExpensesStep
          formData={formData}
          formErrors={{}}
          onInputChange={onInputChange}
          onNext={onNext}
          calculateTotalMonthlyExpenses={() => calculations.monthlyExpenses}
        />
      );
    case 3:
      return (
        <TimeOffStep
          formData={formData}
          formErrors={{}}
          onInputChange={onInputChange}
          onNext={onNext}
          calculateWorkingHours={() => calculations.workHours}
        />
      );
    case 4:
      return (
        <RateWhispererResultsStep
          formData={formData}
          calculations={displayCalculations}
          onStartOver={onStartOver}
        />
      );
    default:
      return null;
  }
};
